import Vue from 'vue';
import PermissionService from '@/services/PermissionService';

/**
 * Directiva v-field-permission para controlar acceso a campos basado en permisos
 * 
 * Ejemplo de uso:
 * <v-text-field v-field-permission="{domain: 'Location', field: 'email', action: 'write'}" />
 * <v-text-field v-field-permission="{domain: 'Location', field: 'email', actions: ['write', 'read']}" />
 */
export const FieldPermission = {
  // Flag para prevenir actualizaciones recursivas
  _disabledComponents: new WeakMap(),
  
  bind(el: HTMLElement, binding: any, vnode: any) {
    applyPermissionLogic(el, binding, vnode);
  },
  
  // Solo actualizar la directiva cuando el binding realmente cambie
  update(el: HTMLElement, binding: any, vnode: any, oldVnode: any) {
    // Evitar actualizaciones innecesarias que puedan causar bucles
    if (JSON.stringify(binding.value) === JSON.stringify(binding.oldValue)) return;
    
    applyPermissionLogic(el, binding, vnode);
  },

  // Limpiar cuando se desmonte el componente
  unbind(el: HTMLElement, binding: any, vnode: any) {
    if (vnode.componentInstance) {
      FieldPermission._disabledComponents.delete(vnode.componentInstance);
    }
  },

  // Asegurarse de aplicar la lógica en inserted para garantizar que el DOM esté listo
  inserted(el: HTMLElement, binding: any, vnode: any) {
    applyPermissionLogic(el, binding, vnode);
  },

  /**
   * Método especial que asegura que la directiva se aplique cuando
   * el componente está completamente montado en el DOM
   */
  componentUpdated(el: HTMLElement, binding: any, vnode: any) {
    // Re-aplicar la lógica para asegurar que las clases se apliquen después de que
    // el componente haya sido completamente renderizado
    applyPermissionLogic(el, binding, vnode);
  }
};

function applyPermissionLogic(el: HTMLElement, binding: any, vnode: any) {
  // Obtener configuración de la directiva, con soporte para action o actions
  const { 
    domain, 
    field, 
    action, 
    actions, 
    mode = 'any', 
    hideIfNoAccess = false 
  } = binding.value;
  
  // Determinar qué acciones verificar (priorizar 'actions' si está presente)
  const actionsToCheck = actions || action || 'write';
  
  // Verificar si el usuario tiene permiso para este campo con las acciones especificadas
  const isDisabled = PermissionService.isFieldDisabled(domain, field, actionsToCheck);
  
  // Si no está deshabilitado, no hacer nada
  if (!isDisabled) return;
  
  if (hideIfNoAccess) {
    // Ocultar el elemento si se solicitó
    el.style.display = 'none';
    return;
  }
  
  // Para elementos HTML estándar (no componentes Vue)
  if (!vnode.componentInstance) {
    disableStandardElement(el);
    return;
  }
  
  // Para componentes Vue/Vuetify
  disableVuetifyComponent(el, vnode.componentInstance);
}

// Función para deshabilitar elementos HTML estándar
function disableStandardElement(el: HTMLElement) {
  // Aplicar atributos estándar de deshabilitado
  el.setAttribute('disabled', 'true');
  el.setAttribute('aria-disabled', 'true');
  
  // Deshabilitar cualquier input anidado
  const inputs = el.querySelectorAll('input, select, textarea, button');
  inputs.forEach(input => {
    input.setAttribute('disabled', 'true');
    input.setAttribute('aria-disabled', 'true');
  });
  
  // Prevenir eventos de click/hover
  if (!el.style.pointerEvents) {
    el.style.pointerEvents = 'none';
  }
}

// Función específica para deshabilitar componentes Vuetify
function disableVuetifyComponent(el: HTMLElement, componentInstance: any) {
  // Evitar aplicar restricciones múltiples veces al mismo componente
  if (FieldPermission._disabledComponents.get(componentInstance)) return;
  
  // Marcar el componente como procesado
  FieldPermission._disabledComponents.set(componentInstance, true);
  
  // 1. Emitir eventos de Vue para actualizar el prop disabled
  if (typeof componentInstance.$emit === 'function') {
    componentInstance.$emit('update:disabled', true);
  }
  
  // 2. NO modificar directamente la propiedad disabled (causa la advertencia)
  // Eliminamos esta línea que causa la advertencia:
  // if (componentInstance.disabled !== undefined) {
  //   Vue.set(componentInstance, 'disabled', true);
  // }

  // 3. Función mejorada para encontrar y aplicar la clase a todos los elementos relevantes
  function findAndDisableVuetifyInputs() {
    // Buscar correctamente el componente Vuetify principal (el elemento raíz)
    // Esta es la parte más crítica
    const rootElement = findVuetifyRootElement(el, componentInstance);
    
    // Si encontramos un elemento raíz, aplicar las clases directamente a él
    if (rootElement) {
      applyDisabledClassesToElement(rootElement);
      
      // También aplicar a sus hijos
      const nestedInputs = rootElement.querySelectorAll('.v-input, .v-select, .v-text-field, .v-checkbox, .v-radio, .v-textarea');
      nestedInputs.forEach(input => applyDisabledClassesToElement(input as HTMLElement));
    } else {
      // Si no encontramos el elemento raíz, aplicar las clases al elemento actual
      applyDisabledClassesToElement(el);
    }
    
    // También deshabilitar todos los inputs dentro del elemento
    const inputs = el.querySelectorAll('input, select, textarea, button');
    inputs.forEach(input => {
      input.setAttribute('disabled', 'true');
      input.setAttribute('aria-disabled', 'true');
    });
  }
  
  // Aplicar las clases de deshabilitado a un elemento específico
  function applyDisabledClassesToElement(element: HTMLElement) {
    // Siempre aplicar la clase base
    element.classList.add('v-input--is-disabled');
    
    // Aplicar clases específicas según el tipo de componente
    if (element.classList.contains('v-text-field')) {
      element.classList.add('v-text-field--is-disabled');
    }
    if (element.classList.contains('v-select')) {
      element.classList.add('v-select--is-disabled');
    }
    if (element.classList.contains('v-autocomplete')) {
      element.classList.add('v-autocomplete--is-disabled');
    }
    if (element.classList.contains('v-combobox')) {
      element.classList.add('v-combobox--is-disabled');
    }
    
    // Añadir clases a las etiquetas
    const labels = element.querySelectorAll('.v-label');
    labels.forEach(label => {
      label.classList.add('v-label--is-disabled');
    });
    
    // Añadir clases a los iconos
    const icons = element.querySelectorAll('.v-icon');
    icons.forEach(icon => {
      icon.classList.add('v-icon--disabled');
    });
  }
  
  // Función para encontrar el elemento raíz de Vuetify
  function findVuetifyRootElement(el: HTMLElement, componentInstance: any): HTMLElement | null {
    // 1. Intentar desde el componentInstance.$el
    if (componentInstance.$el && componentInstance.$el.classList && 
        (componentInstance.$el.classList.contains('v-input') || componentInstance.$el.matches('.v-input'))) {
      return componentInstance.$el;
    }
    
    // 2. Intentar con el elemento actual
    if (el.classList.contains('v-input')) {
      return el;
    }
    
    // 3. Buscar hacia arriba
    const parentInput = el.closest('.v-input') as HTMLElement;
    if (parentInput) {
      return parentInput;
    }
    
    // 4. Buscar en el padre del componente
    if (componentInstance.$parent && componentInstance.$parent.$el) {
      const parentEl = componentInstance.$parent.$el;
      if (parentEl.classList && parentEl.classList.contains('v-input')) {
        return parentEl;
      }
    }
    
    // Si todo falla, devolver null
    return null;
  }
  
  // Aplicar inmediatamente
  findAndDisableVuetifyInputs();
  
  // Y también en el próximo tick para asegurar que la estructura del DOM esté completa
  Vue.nextTick(() => {
    findAndDisableVuetifyInputs();
    
    // Forzar actualización
    if (componentInstance.$forceUpdate) {
      componentInstance.$forceUpdate();
    }
  });
  
  // También aplicar después de un pequeño retraso para componentes más complejos
  setTimeout(() => {
    findAndDisableVuetifyInputs();
  }, 50);
}

// Registrar la directiva globalmente
Vue.directive('field-permission', FieldPermission);

export default FieldPermission;
